import { Space, Typography } from "antd";
import type { ReactNode } from "react";

import type { SectionKey } from "@/shared/config";
import { SECTIONS } from "@/shared/config";

import { SectionIcon } from "./SectionIcon";

/**
 * Шапка страницы: иконка раздела, заголовок, подпись и слот под действия.
 *
 * До неё каждая из тринадцати страниц собирала заголовок сама — `Space` с
 * `justifyContent: "space-between"` и `Typography.Title level={4}` с
 * обнулённым margin. Отступы и уровень заголовка успели разойтись, а на узком
 * экране фильтры в правом слоте наезжали на заголовок.
 *
 * `wrap` на внешнем `Space` не случаен: на странице заявок в `extra` уезжают
 * сегментированный фильтр и поиск, вместе они шире половины экрана ноутбука.
 */
export function PageHeader({
  section,
  title,
  subtitle = null,
  extra = null,
  before = null,
}: {
  section: SectionKey;
  title: ReactNode;
  subtitle?: ReactNode;
  /** Фильтры и кнопки справа. */
  extra?: ReactNode;
  /** Кнопка «назад» и подобное — левее иконки раздела. */
  before?: ReactNode;
}) {
  const { color, Icon } = SECTIONS[section];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 12,
        marginBottom: 16,
      }}
    >
      <Space size={10} align="center">
        {before}
        <SectionIcon color={color}>
          <Icon />
        </SectionIcon>
        <div>
          <Typography.Title level={4} style={{ margin: 0, lineHeight: 1.2 }}>
            {title}
          </Typography.Title>
          {subtitle !== null && (
            <Typography.Text type="secondary">{subtitle}</Typography.Text>
          )}
        </div>
      </Space>

      {extra !== null && (
        // `marginInlineStart: auto` держит блок справа и после переноса: без
        // него на узком экране фильтры прыгают под заголовок к левому краю,
        // и на соседних разделах они оказываются по разные стороны.
        <Space size={8} wrap style={{ marginInlineStart: "auto" }}>
          {extra}
        </Space>
      )}
    </div>
  );
}
